import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import type { TConta } from '@/features/proposta/services/get-pessoa-juridica-por-cnpj'

export type FetchResult<TPatch> = { type: 'SUCCESS'; patch: TPatch } | { type: 'NOT_FOUND' } | { type: 'ERROR' }

export class ClienteJuridicoFormMapper {
  constructor(
    private readonly compradores: TCompradoresCadastrados,
    private readonly emailsJaVinculados: Record<string, string>,
    private readonly nacionalidadePrincipalId: string,
  ) {}

  private documentoIgual(cnpj: string, cnpjRaw: string): boolean {
    return cnpj.replaceAll(/\D/g, '') === cnpjRaw.replaceAll(/\D/g, '')
  }

  validarAvanco(cpf: string, buscouCnpj: boolean): { ok: boolean; error?: 'MISSING_CNPJ' | 'NEED_SEARCH' } {
    if (!cpf) return { ok: false, error: 'MISSING_CNPJ' }
    if (!buscouCnpj) return { ok: false, error: 'NEED_SEARCH' }
    return { ok: true }
  }

  private obterResponsavelDefault(): NonNullable<CompradorPessoaJuridicaSchemaInput['responsavel']> {
    return {
      tipoDoCliente: 'Contato',
      emailsJaVinculados: this.emailsJaVinculados,
      tipo: 'Representante',
      imagem: null,
      dados: {
        cpf: '',
        nome: '',
        profissaoId: '',
        nacionalidadeId: this.nacionalidadePrincipalId,
        maiorDe18Anos: 'sim',
        buscouCpf: false,
        email: null,
        id: '',
      },
      estadoCivil: {
        status: null,
        regimeCasamento: null,
        uniaoEstavel: null,
        tipoAssinatura: undefined,
      },
      contato: {
        email: '',
        telefone: {
          origem: 'Brasil',
          numero: '',
          principal: true,
        },
      },
    }
  }

  obterPatchClienteJuridico(clienteJuridico: TConta): CompradorPessoaJuridicaSchemaInput {
    return {
      emailsJaVinculados: this.emailsJaVinculados,
      tipo: 'PessoaJuridica',
      mode: 'create',
      dados: {
        emailsJaVinculados: this.emailsJaVinculados,
        id: clienteJuridico.id,
        cnpj: clienteJuridico.cnpj,
        razaoSocial: clienteJuridico.razaoSocial,
        buscouCnpj: !!clienteJuridico.cnpj,
        email: clienteJuridico.email ?? '',
        telefone: clienteJuridico.telefone
          ? {
              numero: clienteJuridico.telefone.numero,
              origem: clienteJuridico.telefone.origem,
            }
          : {
              numero: '',
              origem: 'Brasil',
            },
      },
      responsavel: this.obterResponsavelDefault(),
    }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TConta, AxiosError>,
  ): FetchResult<Partial<CompradorPessoaJuridicaSchemaInput>> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) return { type: 'NOT_FOUND' }
    if (result.isSuccess && result.data) {
      return { type: 'SUCCESS', patch: this.obterPatchClienteJuridico(result.data) }
    }
    return { type: 'ERROR' }
  }

  devePularBuscaPoisHaDuplicidade(cpfRaw: string): boolean {
    return this.compradores.some((c) => this.documentoIgual(c.documento, cpfRaw))
  }

  limparFormulario(): CompradorPessoaJuridicaSchemaInput {
    return {
      emailsJaVinculados: this.emailsJaVinculados,
      dados: {
        emailsJaVinculados: this.emailsJaVinculados,
        cnpj: '',
        razaoSocial: '',
        buscouCnpj: false,
        email: '',
        telefone: {
          numero: '',
          origem: 'Brasil',
        },
      },
      tipo: 'PessoaJuridica',
      mode: 'create',
      responsavel: this.obterResponsavelDefault(),
    }
  }
}
